import { AxiosInstance } from 'axios'

import Constants from '../../lib/Constants'
import { reduceAllSettledResponse } from '../../utils'
import logger from '../../utils/logger'
import { ConversationLegacyRequests } from '../interfaces/ConversationsLegacy'
import Resource from '../resource'

const actions = (client: AxiosInstance): ConversationLegacyRequests => {
  // TODO: Fix this URL, it's stale / broken.
  // https://github.com/signpost/gravy/blob/master/apps/lambda/Gravy.js#L1629
  const sendMessage: ConversationLegacyRequests['sendMessage'] = async (
    body,
    medias,
    channelId,
    customerId,
    // TODO: Rename this parameter to something more clear, since the actual value passed
    // to it is sometimes just e.g. "AgentzInteraction", not an id like "AgentzInteraction.123".
    interactionId,
    documentType,
    asReplyTo
  ) => {
    logger.debug('Called sendMessage', {
      channelId,
      customerId,
      interactionId,
      documentType,
      asReplyTo,
    })
    let method: string

    const isThumbtackReplyMethodEnabled = !!localStorage.getItem(
      'SP_ENABLE_THUMBTACK_METHOD'
    )

    if (
      Constants.COMMS.getCommTypes({
        commDocTypes: 'smsDocumentTypes',
        isThumbtackReplyMethodEnabled,
      }).includes(documentType)
    ) {
      method = 'sms'
    } else if (Constants.COMMS.agentzDocumentTypes.includes(documentType)) {
      method = 'agentz'
    } else if (Constants.COMMS.facebookDocumentTypes.includes(documentType)) {
      method = 'facebook'
    } else if (
      Constants.COMMS.googleMessagesDocumentTypes.includes(documentType)
    ) {
      method = 'googleMessages'
    } else if (
      isThumbtackReplyMethodEnabled &&
      Constants.COMMS.thumbtackDocumentTypes.includes(documentType)
    ) {
      method = 'thumbtack'
    } else {
      throw new Error(`Unsupported document type: ${documentType}`)
    }

    const transformedMedias = medias.map((media) => ({
      contentType: media.contentType,
      url: `${media.url.match(new RegExp(/(.*)(\?.*)/))![1]}`,
    }))

    logger.debug('Sending message via method', {
      channelId,
      customerId,
      interactionId,
      documentType,
      asReplyTo,
      method,
    })

    const requestToSend = (
      sendingMedia: {
        contentType: string
        url: string
      }[]
    ) => {
      return client.post<{ fingerprint: string }, { fingerprint: string }>(
        Constants.Backend.Endpoints.CORE_CLIENT_PROXY,
        {
          module: 'Customers',
          method: 'sendIndividualComm',
          params: {
            channelId,
            customerId,
            method,
            asReplyTo,
            content: {
              body,
              media: sendingMedia.length ? sendingMedia : undefined,
              endLiveSupport: false,
            },
            overrideFingerprint: true,
          },
        }
      )
    }

    let requests: Promise<{ fingerprint: string }>[] = [
      requestToSend(transformedMedias),
    ]

    if (method === 'facebook' && transformedMedias.length) {
      requests = transformedMedias.map((sendingMedia) =>
        requestToSend([sendingMedia])
      )
    }

    const responses = await Promise.allSettled(requests)

    const [fulfilled, rejected] = reduceAllSettledResponse(responses)

    if (rejected.length && !fulfilled.length) {
      throw new Error('Images failed to send')
    }

    return fulfilled[0].value
  }

  const getConversationsPreviews: ConversationLegacyRequests['getConversationsPreviews'] =
    (conversationsIds) => {
      return client.post(Constants.Backend.Endpoints.CORE_CLIENT_PROXY, {
        module: 'Documents',
        method: 'mgetByKey',
        params: {
          scope: 'ChannelEvents',
          key: 'docId',
          values: conversationsIds,
        },
      })
    }

  return {
    sendMessage,
    markConversationRead: async (contactId: number | number[], read = true) => {
      const contactIds = Array.isArray(contactId) ? contactId : [contactId]

      // TODO: Batches the requests, and waits for one batch to resolve before triggering the next.
      // Similar to https://github.com/signpost/essentials/blob/1e6f60cfb136248b2783c61fbcc4423a5f244c22/Sequencer.js#L165
      const results = await Promise.allSettled(
        contactIds.map(async (_contactId) =>
          client.post(Constants.Backend.Endpoints.CORE_CLIENT_PROXY, {
            module: 'Customers',
            method: 'updateConversation',
            params: {
              customerId: _contactId,
              read,
            },
          })
        )
      )

      return reduceAllSettledResponse(results)
    },
    updateArchiveStatus: async (
      contactId: number | number[],
      archived = true
    ) => {
      const contactIds = Array.isArray(contactId) ? contactId : [contactId]

      // TODO: Batches the requests, and waits for one batch to resolve before triggering the next.
      // Similar to https://github.com/signpost/essentials/blob/1e6f60cfb136248b2783c61fbcc4423a5f244c22/Sequencer.js#L165
      const results = await Promise.allSettled(
        contactIds.map(async (_contactId) =>
          client.post(Constants.Backend.Endpoints.CORE_CLIENT_PROXY, {
            module: 'Customers',
            method: 'updateConversation',
            params: {
              customerId: _contactId,
              archived,
            },
          })
        )
      )

      return reduceAllSettledResponse(results)
    },
    updateUnconfirmedReviewArchiveStatus: async (
      reviewIds,
      locationId = 0,
      archived = true
    ) => {
      // TODO: Batches the requests, and waits for one batch to resolve before triggering the next.
      // Similar to https://github.com/signpost/essentials/blob/1e6f60cfb136248b2783c61fbcc4423a5f244c22/Sequencer.js#L165
      const results = await Promise.allSettled(
        reviewIds.map(async (reviewId) =>
          client.post(Constants.Backend.Endpoints.CORE_CLIENT_PROXY, {
            module: 'LocationReviewSites',
            method: 'toggleHidePublicReview',
            params: {
              locationId,
              publicReviewId: reviewId,
              hiddenFromUi: archived,
            },
          })
        )
      )

      return reduceAllSettledResponse(results)
    },
    getConversationsPreviews,
  }
}

export default Resource(actions)
